import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminSupportingText,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  APPEALS_CONFIG_ROUTE,
  buildSampleAppealRules,
  formatAppealsDateTime,
  getAppealAvailabilityLabel,
  getApprovedActionLabel,
  getAccountTypeDisplay,
  getGeneratedAccessSummary,
  getDecisionTypeLabel,
  getSourceModuleLabel,
  normalizeAppealForm,
  sortAppealRules,
} from '../../../data/appealsConfig';

const tableCols =
  'minmax(0, 0.55fr) minmax(0, 0.75fr) minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1.15fr) minmax(0, 1.1fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(62px, 0.04fr)';

const Appeals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => sortAppealRules(buildSampleAppealRules()));

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.rules ?? location.state?.configurations;
    if (Array.isArray(incoming)) {
      setRules(sortAppealRules(incoming.map(normalizeAppealForm)));
      navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  const openView = (rule) => {
    navigate(`${APPEALS_CONFIG_ROUTE}/${encodeURIComponent(rule.id)}`, {
      state: { rule, configuration: rule, existingRules: rules, configurations: rules },
    });
  };

  const openEdit = (rule) => {
    navigate(`${APPEALS_CONFIG_ROUTE}/${rule.id}/edit`, {
      state: { rule, configuration: rule, existingRules: rules, configurations: rules },
    });
  };

  const columnHeaders = [
    'Rule ID',
    'Module',
    'Decision',
    'Account',
    'Entry Point',
    'Approved Action',
    'Availability',
    'Date Added',
    'Last Updated',
    'Actions',
  ];

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Appeal</AdminPageTitle>
          <AdminHeaderAction
            onClick={() =>
              navigate(`${APPEALS_CONFIG_ROUTE}/add`, {
                state: { existingRules: rules, configurations: rules },
              })
            }
          >
            Add Rule
          </AdminHeaderAction>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: tableCols }}
            >
              {columnHeaders.map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0 ${
                    label === 'Actions' ? 'justify-center shrink-0' : ''
                  }`}
                >
                  <AdminTableHeader className={label === 'Actions' ? '' : 'truncate'}>
                    {label}
                  </AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRules.length === 0 ? (
              <div className="text-center py-10 px-4">
                <AdminSupportingText className="text-mediumgray">
                  No appeal rules found.
                </AdminSupportingText>
              </div>
            ) : (
              paginatedRules.map((rule) => {
                const normalized = normalizeAppealForm(rule);
                const actionLabel = getApprovedActionLabel(
                  normalized.sourceModule,
                  normalized.decisionType,
                  normalized.approvedCorrectiveAction
                );
                const accessLabel = getGeneratedAccessSummary(normalized.accountTypes);
                return (
                  <div
                    key={normalized.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openView(normalized)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openView(normalized);
                      }
                    }}
                    className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                    style={{ gridTemplateColumns: tableCols }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={normalized.id}>
                        {normalized.id}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">
                        {getSourceModuleLabel(normalized.sourceModule)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody
                        className="truncate block"
                        title={getDecisionTypeLabel(
                          normalized.sourceModule,
                          normalized.decisionType
                        )}
                      >
                        {getDecisionTypeLabel(normalized.sourceModule, normalized.decisionType)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">
                        {getAccountTypeDisplay(normalized.accountTypes)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={accessLabel}>
                        {accessLabel}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={actionLabel}>
                        {actionLabel}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">
                        {getAppealAvailabilityLabel(normalized.appealAvailability)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody
                        className="truncate block"
                        title={formatAppealsDateTime(normalized.dateAdded)}
                      >
                        {formatAppealsDateTime(normalized.dateAdded)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody
                        className="truncate block"
                        title={formatAppealsDateTime(normalized.lastUpdated)}
                      >
                        {formatAppealsDateTime(normalized.lastUpdated)}
                      </AdminTableBody>
                    </div>
                    <div
                      className="flex items-center justify-center shrink-0 overflow-visible"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[normalized.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(normalized.id);
                          }}
                          aria-expanded={!!rowActionsOpen[normalized.id]}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[normalized.id] && rowActionsMenuLayout ? (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openView(normalized);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openEdit(normalized);
                              }}
                            >
                              Edit
                            </AdminRowMenuItem>
                          </AdminFixedRowActionsMenu>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={rules.length > 0 && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Appeals;
